import { Link, useParams } from "react-router-dom";
import Breadcrumbs from "@/components/base/Breadcrumbs";
import LeadCard from "@/components/feature/LeadCard";
import { useSeo } from "@/hooks/useSeo";
import { leads } from "@/mocks/leads";
import { siteInfo } from "@/mocks/site";

export default function LeadDetailPage() {
  const { id } = useParams<{ id: string }>();
  const lead = leads.find((item) => item.id === id);

  useSeo({
    title: lead ? `${lead.title} | ${lead.category}线索 - 直曝` : "线索未找到 - 直曝",
    description: lead
      ? `${lead.summary}（${lead.category}线索 · ${lead.date} 发布 · 阅读约 ${lead.readTime}）`
      : "该线索可能已下架或被合并，请返回爆料线索库查看其他内容。",
    keywords: lead
      ? `${lead.tags.join(",")},网络爆料,网络爆料线索,${lead.category}`
      : "网络爆料,网络爆料线索",
    path: `/leads/${id ?? ""}`,
    type: "article",
    jsonLd: lead
      ? [
          {
            "@context": "https://schema.org",
            "@type": "Article",
            headline: lead.title,
            description: lead.summary,
            author: { "@type": "Organization", name: "直曝编辑部" },
            publisher: { "@type": "Organization", name: siteInfo.fullName },
            datePublished: lead.date,
            dateModified: lead.date,
            inLanguage: "zh-CN",
            articleSection: lead.category,
            keywords: lead.tags.join(","),
            mainEntityOfPage: `https://ygtabln.cn/leads/${lead.id}`,
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "首页", item: "https://ygtabln.cn/" },
              { "@type": "ListItem", position: 2, name: "爆料线索库", item: "https://ygtabln.cn/leads" },
              {
                "@type": "ListItem",
                position: 3,
                name: lead.title,
                item: `https://ygtabln.cn/leads/${lead.id}`,
              },
            ],
          },
        ]
      : undefined,
  });

  if (!lead) {
    return (
      <div className="px-4 md:px-6 py-20 md:py-28">
        <div className="max-w-3xl mx-auto text-center">
          <span className="mx-auto w-14 h-14 rounded-full bg-background-200 text-foreground-500 flex items-center justify-center">
            <i className="ri-file-unknow-line text-2xl" aria-hidden="true"></i>
          </span>
          <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-950">这条线索不在了</h1>
          <p className="mt-3 text-sm text-foreground-600">
            它可能已经下架、被合并，或者链接有一处笔误。返回线索库看看其他内容吧。
          </p>
          <Link
            to="/leads"
            className="mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-md bg-primary-500 text-background-50 text-sm font-label font-medium whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
          >
            返回爆料线索库
          </Link>
        </div>
      </div>
    );
  }

  const related = leads.filter((item) => item.id !== lead.id).slice(0, 3);
  const sameCategory = related.filter((item) => item.categorySlug === lead.categorySlug);
  const recommendations = [...sameCategory, ...related.filter((item) => item.categorySlug !== lead.categorySlug)].slice(0, 3);

  return (
    <>
      <div className="bg-background-100 border-b border-background-200">
        <div className="px-4 md:px-6 py-6 md:py-9">
          <div className="max-w-4xl mx-auto">
            <Breadcrumbs items={[{ label: "爆料线索库", href: "/leads" }, { label: lead.category }]} />
          </div>
        </div>
      </div>

      <article className="px-4 md:px-6 py-10 md:py-14">
        <div className="max-w-4xl mx-auto">
          <header>
            <div className="flex flex-wrap items-center gap-2">
              <Link
                to={`/leads?category=${lead.categorySlug}`}
                className="text-xs px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-900 font-label hover:bg-secondary-200 transition-colors cursor-pointer"
              >
                {lead.category}
              </Link>
              <span className="text-xs text-foreground-500 font-label">发布：{lead.date}</span>
              <span className="text-xs text-foreground-500 font-label">阅读约 {lead.readTime}</span>
              <span className="text-xs text-foreground-500 font-label">
                {lead.views.toLocaleString()} 次阅读
              </span>
            </div>

            <h1 className="mt-4 font-heading text-2xl md:text-4xl font-bold text-foreground-950 leading-snug md:leading-[1.3]">
              {lead.title}
            </h1>

            <p className="mt-5 text-base text-foreground-700 leading-relaxed border-l-2 border-primary-400 pl-4">
              {lead.summary}
            </p>

            <div className="mt-6 flex items-center gap-3 text-sm text-foreground-600 font-label">
              <span className="w-9 h-9 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center">
                <i className="ri-draft-line text-lg" aria-hidden="true"></i>
              </span>
              <span>{lead.author}</span>
              <span className="text-foreground-300">|</span>
              <span className="flex items-center gap-1.5">
                <i className="ri-shield-check-line w-4 h-4 flex items-center justify-center" aria-hidden="true"></i>
                已匿名化处理
              </span>
            </div>
          </header>

          <figure className="mt-8">
            <div className="w-full h-56 md:h-96 rounded-lg overflow-hidden bg-background-200">
              <img
                src={lead.cover}
                alt={`${lead.title} - ${lead.category}线索配图`}
                className="w-full h-full object-cover"
              />
            </div>
            <figcaption className="mt-2.5 text-xs text-foreground-500 font-label">
              配图为示意图，与线索具体人物、地点无关
            </figcaption>
          </figure>

          <div className="mt-10 grid grid-cols-1 lg:grid-cols-12 gap-8">
            <div className="lg:col-span-8">
              {lead.content.map((section, index) => (
                <section key={section.heading} id={`section-${index}`} className="mb-8 scroll-mt-28">
                  <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-950 flex items-start gap-2.5">
                    <span className="mt-1.5 w-1.5 h-4 rounded-full bg-primary-500 shrink-0"></span>
                    {section.heading}
                  </h2>
                  <div className="mt-3 space-y-4">
                    {section.paragraphs.map((paragraph) => (
                      <p key={paragraph} className="text-[15px] md:text-base text-foreground-700 leading-[1.9]">
                        {paragraph}
                      </p>
                    ))}
                  </div>
                </section>
              ))}

              <div className="mt-8 flex flex-wrap items-center gap-2">
                <span className="text-xs text-foreground-500 font-label mr-1">相关标签：</span>
                {lead.tags.map((tag) => (
                  <Link
                    key={tag}
                    to={`/leads?keyword=${encodeURIComponent(tag)}`}
                    className="text-xs px-3 py-1.5 rounded-full bg-background-100 border border-background-200 text-foreground-700 hover:border-accent-400 hover:text-accent-800 transition-colors cursor-pointer"
                  >
                    {tag}
                  </Link>
                ))}
              </div>
            </div>

            <aside className="lg:col-span-4">
              <div className="lg:sticky lg:top-28 flex flex-col gap-5">
                <nav className="rounded-lg border border-background-200 bg-background-100 p-5" aria-label="文章目录">
                  <h2 className="font-heading text-sm font-bold text-foreground-950">本篇目录</h2>
                  <ol className="mt-3 space-y-2.5">
                    {lead.content.map((section, index) => (
                      <li key={section.heading}>
                        <a
                          href={`#section-${index}`}
                          className="flex items-start gap-2 text-sm text-foreground-600 hover:text-primary-600 transition-colors cursor-pointer"
                        >
                          <span className="text-xs font-label text-foreground-400 mt-0.5">
                            {String(index + 1).padStart(2, "0")}
                          </span>
                          <span className="leading-snug">{section.heading}</span>
                        </a>
                      </li>
                    ))}
                  </ol>
                </nav>

                <div className="rounded-lg bg-primary-600 p-5 text-background-50">
                  <h2 className="font-heading text-base font-bold flex items-center gap-2">
                    <i className="ri-send-plane-fill w-5 h-5 flex items-center justify-center" aria-hidden="true"></i>
                    你也有类似的经历？
                  </h2>
                  <p className="mt-2.5 text-sm text-primary-50/90 leading-relaxed">
                    写下你知道的事，不需要留下姓名。编辑会先判断，再决定下一步。
                  </p>
                  <Link
                    to="/submit"
                    className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-background-50 text-primary-700 text-sm font-label font-medium whitespace-nowrap hover:bg-accent-100 transition-colors cursor-pointer"
                  >
                    匿名投稿
                  </Link>
                </div>

                <div className="rounded-lg border border-background-200 p-5">
                  <h2 className="font-heading text-sm font-bold text-foreground-950">编辑邮箱</h2>
                  <p className="mt-2 text-sm text-foreground-600 break-all">
                    <a href={`mailto:${siteInfo.email}`} className="hover:text-primary-600 transition-colors cursor-pointer">
                      {siteInfo.email}
                    </a>
                  </p>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </article>

      <section className="px-4 md:px-6 py-12 md:py-16 bg-background-100 border-t border-background-200" aria-labelledby="related-title">
        <div className="max-w-6xl mx-auto">
          <h2 id="related-title" className="font-heading text-xl md:text-2xl font-bold text-foreground-950">
            相关线索推荐
          </h2>
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {recommendations.map((item) => (
              <LeadCard key={item.id} lead={item} />
            ))}
          </div>
        </div>
      </section>
    </>
  );
}